import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Lock,
  User,
  Upload,
  Image,
  Mic,
  Square,
  Newspaper,
  FileText,
  Save,
  X,
  Eye,
  EyeOff,
  CheckCircle,
  Send,
  Paperclip,
  Sparkles,
  RotateCcw,
  Edit3,
  Hash,
  Megaphone,
  Layers,
  Brain,
  Cpu,
  Zap,
  AlertTriangle,
  Info,
  AlertCircle,
  Loader2,
  BadgeCheck,
} from "lucide-react";

const STATIC_USERNAME = "radhAI.clone";
const STATIC_PASSWORD = "ceovoice";

const API_BASE_URL = "https://mailautomation-production.up.railway.app/api";

const COMPANY_UPLOAD_API = `${API_BASE_URL}/v1/upload/company`;
const CONTENT_SUBMIT_API = `${API_BASE_URL}/v1/content/submit`;
const ADD_TO_CLONE_API = `${API_BASE_URL}/v1/add-to-clone`;
const IMAGE_API = `${API_BASE_URL}/v1/image`;
const BLOG_FORMAT_API = `${API_BASE_URL}/v1/blog/format`;
const BLOG_PUBLISH_API = `${API_BASE_URL}/v1/blog/publish`;
const FILE_BASE_URL = "https://meta.oxyloans.com/radha-ai/files";

const disclaimerText = `\n\n### ✅ **Blog Disclaimer**\n*This blog is AI-assisted and based on public data. We aim to inform, not infringe. Contact us for edits or collaborations: [support@askoxy.ai]*`;

const PLATFORMS = [
  { key: "OXY_LOANS", label: "OxyLoans" },
  { key: "OXY_BRICKS", label: "OxyBricks" },
  { key: "OXY_GOLD_AI", label: "OxyGold" },
  { key: "ASK_OXY_AI", label: "ASKOXY.AI" },
  { key: "STUDY_ABROAD", label: "Study Abroad" },
  { key: "OXY_GLOBAL", label: "OxyGlobal" },
  { key: "OTHER", label: "Other" },
];

type GeneratedSection = {
  heading?: string;
  body?: string;
};

type ParsedGeneratedContent = {
  title?: string;
  intro?: string;
  sections?: GeneratedSection[];
  closing?: string;
hashtags?: string | string[];
  callToAction?: string;
  isGrouped?: boolean;
};

type SubmitResponse = {
  success: boolean;
  message: string;
  data?: {
    contentId?: string;
    generatedContent?: string;
    editedContent?: string | null;
    context?: string;
    generatedContext?: string;
    rawContent?: string;
  };
};

type EntityType = "CONTENT" | "VIDEO";

type BlogPayload = {
  entityId?: string;
  entityType?: EntityType;
  title?: string;
  description?: string;
  socialMediaCaptions?: string;
  addedBy?: string;
  videoUrl?: string;
  videoFileUrl?: string | null;
  imageUrl?: string | null;
  status?: string;
  blogPostId?: string | null;
};

const getEntityId = (data: any) =>
  String(data?.entityId || data?.contentId || data?.videoId || data?.id || "");

const getImageUrl = (url?: string | null) => {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  if (url.startsWith("/uploads/images/")) return `${FILE_BASE_URL}${url}`;
  if (url.startsWith("uploads/images/")) return `${FILE_BASE_URL}/${url}`;
  return url;
};

// ─── Centered Confirm Modal ────────────────────────────────────────────────
function ConfirmModal({
  open,
  title,
  description,
  confirmLabel,
  cancelLabel,
  confirmColor,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  description: string;
  confirmLabel: string;
  cancelLabel?: string;
  confirmColor?: "green" | "red";
  onConfirm: () => void;
  onCancel: () => void;
}) {
  if (!open) return null;
  const isGreen = confirmColor !== "red";
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[200] flex items-center justify-center bg-black/70 px-4 backdrop-blur-md"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.88, y: 24 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.88, y: 24 }}
          transition={{ type: "spring", stiffness: 340, damping: 28 }}
          className="w-full max-w-[420px] rounded-[28px] border border-[#3A465B]/80 bg-[#111A2B] p-6 shadow-[0_40px_120px_rgba(0,0,0,0.7)]"
        >
          <div className="mb-4 flex items-center gap-3">
            <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl ${isGreen ? "bg-[#B6F269]/15" : "bg-red-500/15"}`}>
              {isGreen ? <CheckCircle size={20} className="text-[#B6F269]" /> : <AlertTriangle size={20} className="text-red-400" />}
            </div>
            <h3 className="text-base font-black text-white">{title}</h3>
          </div>
          <p className="mb-6 text-sm leading-6 text-[#9AA7BC]">{description}</p>
          <div className="flex gap-3">
            <button
              onClick={onConfirm}
              className={`flex-1 rounded-2xl py-2.5 text-sm font-black text-black shadow-lg ${
                isGreen
                  ? "bg-gradient-to-br from-[#B6F269] via-[#75E6C9] to-[#5EDDF2] shadow-[0_10px_28px_rgba(94,221,242,0.22)]"
                  : "bg-gradient-to-br from-red-400 to-red-600 text-white shadow-[0_10px_28px_rgba(239,68,68,0.22)]"
              }`}
            >
              {confirmLabel}
            </button>
            <button
              onClick={onCancel}
              className="flex-1 rounded-2xl border border-[#3A465B]/80 bg-[#1A2230] py-2.5 text-sm font-bold text-white"
            >
              {cancelLabel || "Cancel"}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

// ─── Publish Success Modal ─────────────────────────────────────────────────
function PublishSuccessModal({
  open,
  blogPostId,
  onClose,
  onNewContent,
}: {
  open: boolean;
  blogPostId?: string | null;
  onClose: () => void;
  onNewContent: () => void;
}) {
  if (!open) return null;
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[200] flex items-center justify-center bg-black/70 px-4 backdrop-blur-md"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.85, y: 32 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.88, y: 24 }}
          transition={{ type: "spring", stiffness: 300, damping: 26 }}
          className="w-full max-w-[440px] rounded-[28px] border border-lime-300/20 bg-[#0D1B10] p-7 shadow-[0_40px_120px_rgba(0,0,0,0.7)]"
        >
          {/* Animated check */}
          <div className="mb-5 flex flex-col items-center text-center">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 400, damping: 20, delay: 0.1 }}
              className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-[#B6F269] via-[#75E6C9] to-[#5EDDF2] shadow-[0_0_40px_rgba(182,242,105,0.35)]"
            >
              <CheckCircle size={32} className="text-black" />
            </motion.div>
            <h3 className="text-xl font-black text-white">Blog Published!</h3>
            <p className="mt-2 text-sm text-[#9AA7BC]">
              Your blog post has been published successfully.
            </p>
            {blogPostId && blogPostId !== "POSTED SUCCESSFULLY" && (
              <p className="mt-2 rounded-xl border border-lime-300/20 bg-lime-300/8 px-4 py-2 text-xs font-bold text-lime-200">
                Post ID: {blogPostId}
              </p>
            )}
          </div>
          <div className="flex flex-col gap-3">
            <button
              onClick={onNewContent}
              className="flex h-11 w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-br from-[#B6F269] via-[#75E6C9] to-[#5EDDF2] text-sm font-black text-black shadow-[0_12px_30px_rgba(94,221,242,0.22)]"
            >
              <RotateCcw size={15} />
              New Content
            </button>
            <button
              onClick={onClose}
              className="flex h-11 w-full items-center justify-center gap-2 rounded-2xl border border-[#3A465B]/80 bg-[#1A2230] text-sm font-bold text-white"
            >
              OK, Close
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

// ─── Toast (lightweight) ───────────────────────────────────────────────────
function useToast() {
  const [toasts, setToasts] = useState<{ id: number; icon: string; title: string }[]>([]);
  const counter = useRef(0);

  const show = (icon: "success" | "error" | "warning" | "info", title: string) => {
    const id = ++counter.current;
    setToasts((prev) => [...prev, { id, icon, title }]);
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 2800);
  };

  const ToastContainer = () => (
    <div className="fixed right-4 top-4 z-[999] space-y-2">
      <AnimatePresence>
        {toasts.map((t) => {
          const colors = {
            success: "border-lime-400/30 bg-lime-400/10 text-lime-200",
            error: "border-red-400/30 bg-red-500/10 text-red-200",
            warning: "border-yellow-400/30 bg-yellow-400/10 text-yellow-200",
            info: "border-[#5EDDF2]/30 bg-[#5EDDF2]/10 text-[#DDFBFF]",
          }[t.icon] || "border-white/20 bg-white/5 text-white";
          const dot = {
            success: "bg-lime-400",
            error: "bg-red-400",
            warning: "bg-yellow-400",
            info: "bg-[#5EDDF2]",
          }[t.icon] || "bg-white";
          return (
            <motion.div
              key={t.id}
              initial={{ opacity: 0, x: 60, scale: 0.9 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{ opacity: 0, x: 60, scale: 0.9 }}
              className={`flex min-w-[240px] items-center gap-3 rounded-2xl border px-4 py-3 text-sm font-semibold backdrop-blur-md ${colors}`}
            >
              <span className={`h-2 w-2 shrink-0 rounded-full ${dot}`} />
              {t.title}
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );

  return { toast: show, ToastContainer };
}

export default function RadhAICloneAdminPage() {
  const { toast, ToastContainer } = useToast();

  const [isLoggedIn, setIsLoggedIn] = useState(
    typeof sessionStorage !== "undefined" && sessionStorage.getItem("radhAIAdminLogin") === "true"
  );

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState("");

  const [companyPlatform, setCompanyPlatform] = useState("");
  const [submitPlatform, setSubmitPlatform] = useState("");
const [customPlatformName, setCustomPlatformName] = useState("");
const [customCompanyPlatformName, setCustomCompanyPlatformName] = useState("");
  const [rawInstruction, setRawInstruction] = useState("");
  const [interimVoiceText, setInterimVoiceText] = useState("");

  const [attachments, setAttachments] = useState<File[]>([]);
  const [companyFiles, setCompanyFiles] = useState<File[]>([]);
const [showCompanyUploadSuccess, setShowCompanyUploadSuccess] = useState(false);
const [isRecording, setIsRecording] = useState(false);
const [isTranscribing, setIsTranscribing] = useState(false);
const mediaRecorderRef = useRef<MediaRecorder | null>(null);
const audioChunksRef = useRef<Blob[]>([]);
const streamRef = useRef<MediaStream | null>(null);

  const generatedOutputRef = useRef<HTMLDivElement | null>(null);

  const [isCompanyUploading, setIsCompanyUploading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [companyFileAttempted, setCompanyFileAttempted] = useState(false);
  const [submitContentAttempted, setSubmitContentAttempted] = useState(false);

  const [contentId, setContentId] = useState("");
  const [generatedContext, setGeneratedContext] = useState("");
  const [generatedContent, setGeneratedContent] = useState("");
  const [editedContent, setEditedContent] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [parsedEdits, setParsedEdits] = useState<ParsedGeneratedContent | null>(null);

  const [addedToClone, setAddedToClone] = useState(false);
  const [showBlogFlow, setShowBlogFlow] = useState(false);
  const [blogPreview, setBlogPreview] = useState<BlogPayload | null>(null);
  const [publishedBlog, setPublishedBlog] = useState<BlogPayload | null>(null);
  const [imageLoading, setImageLoading] = useState(false);
  const [blogFormatting, setBlogFormatting] = useState(false);
  const [formatLoading, setFormatLoading] = useState(false);
  const [publishLoading, setPublishLoading] = useState(false);
  const [isBlogModalOpen, setIsBlogModalOpen] = useState(false);
  const [isInputExpanded, setIsInputExpanded] = useState(false);

  // Modal states
  const [confirmModal, setConfirmModal] = useState<{
    open: boolean;
    title: string;
    description: string;
    confirmLabel: string;
    confirmColor?: "green" | "red";
    onConfirm: () => void;
  } | null>(null);
  const [publishSuccessModal, setPublishSuccessModal] = useState(false);

  const buttonMotion = {
    whileHover: { y: -2, scale: 1.015 },
    whileTap: { scale: 0.97 },
  };

  useEffect(() => {
    if (!isSubmitting && !generatedContent) return;
    const isMobile = window.innerWidth < 1024;
    if (!isMobile) return;
    const timer = window.setTimeout(() => {
      generatedOutputRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 250);
    return () => window.clearTimeout(timer);
  }, [isSubmitting, generatedContent]);

 const displayText = isTranscribing
  ? ""
  : interimVoiceText
  ? `${rawInstruction}${rawInstruction ? " " : ""}${interimVoiceText}`
  : rawInstruction;

  const hasInputContent = rawInstruction.trim().length > 0 || attachments.length > 0;
  const canSubmit = Boolean(submitPlatform) && hasInputContent && !isSubmitting;
  const canUploadCompanyFiles = Boolean(companyPlatform) && companyFiles.length > 0 && !isCompanyUploading;
  const canAddToClone = Boolean(contentId && generatedContent.trim()) && !isApproving;
  const hasFormattedBlogPreview = Boolean(
    blogPreview?.title?.trim() || blogPreview?.description?.trim() || blogPreview?.socialMediaCaptions?.trim()
  );
  const canPublishBlog = Boolean(blogPreview && hasFormattedBlogPreview && !publishedBlog);

  const parsedGeneratedContent = useMemo<ParsedGeneratedContent | null>(() => {
    if (!generatedContent) return null;
    try {
      const parsed = JSON.parse(generatedContent);
      if (parsed && typeof parsed === "object") return parsed;
      return null;
    } catch {
      return null;
    }
  }, [generatedContent]);

  const currentContentForClone = () => {
    if (isEditing && parsedEdits) return JSON.stringify(parsedEdits);
    return editedContent || generatedContent;
  };

  const buildBlogFallback = (existing?: BlogPayload | null): BlogPayload => {
    const source: ParsedGeneratedContent | null = parsedEdits || parsedGeneratedContent || null;
    const fallbackTitle =
      source?.title ||
      (generatedContent && !parsedGeneratedContent ? generatedContent.slice(0, 90) : "") ||
      "radhAI Generated Blog";
    const sectionText = source?.sections
      ?.map((section) => [section.heading, section.body].filter(Boolean).join("\n"))
      .filter(Boolean)
      .join("\n\n");
    const baseDescription =
      source?.intro || sectionText || source?.closing || editedContent || generatedContext || generatedContent || "Blog content generated from radhAI admin input.";
    const fallbackDescription = baseDescription + disclaimerText;
   const hashtagText =
  typeof source?.hashtags === "string"
    ? source.hashtags
    : Array.isArray(source?.hashtags)
    ? source?.hashtags?.join(" ")
    : "";

const fallbackCaptions = `
${source?.callToAction || ""}

${hashtagText}
`.trim();

    return {
      ...(existing || {}),
      entityId: getEntityId(existing) || contentId,
      entityType: "CONTENT",
      title: existing?.title || fallbackTitle,
      description: existing?.description || fallbackDescription,
      socialMediaCaptions: existing?.socialMediaCaptions || fallbackCaptions,
      addedBy: existing?.addedBy || "Radha",
      imageUrl: existing?.imageUrl || "",
      videoUrl: existing?.videoUrl || "",
      videoFileUrl: existing?.videoFileUrl || null,
      blogPostId: existing?.blogPostId || null,
    };
  };

  const mergeBlogPreview = (apiData: Partial<BlogPayload>, existing?: BlogPayload | null): BlogPayload => {
    const merged = buildBlogFallback({ ...(existing || {}), ...(apiData || {}) });
    // Append disclaimer to description if not already there
    const desc = apiData.description || existing?.description || merged.description || "";
    const finalDesc = desc.includes("Blog Disclaimer") ? desc : desc + disclaimerText;

    return {
      ...merged,
      entityId: getEntityId(apiData) || getEntityId(existing) || contentId,
      entityType: "CONTENT",
      imageUrl: apiData.imageUrl || existing?.imageUrl || merged.imageUrl || "",
      title: apiData.title || existing?.title || merged.title,
      description: finalDesc,
socialMediaCaptions: `
${
  apiData.socialMediaCaptions ||
  existing?.socialMediaCaptions ||
  merged.socialMediaCaptions ||
  ""
}

${
  typeof parsedGeneratedContent?.hashtags === "string"
    ? parsedGeneratedContent.hashtags
    : Array.isArray(parsedGeneratedContent?.hashtags)
    ? parsedGeneratedContent?.hashtags?.join(" ")
    : ""
}
`.trim(),      addedBy: apiData.addedBy || existing?.addedBy || merged.addedBy || "Radha",
    };
  };

  const fetchJson = async (url: string, options?: RequestInit) => {
    const res = await fetch(url, options);
    const text = await res.text();
    let data: any = null;
    try {
      data = text ? JSON.parse(text) : null;
    } catch {
      throw new Error(text || "Invalid server response");
    }
    if (!res.ok || data?.success === false) {
      throw new Error(data?.message || `API failed: ${res.status}`);
    }
    return data;
  };

 const startRecording = async () => {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: true,
    });

    streamRef.current = stream;

    const recorder = new MediaRecorder(stream);

    mediaRecorderRef.current = recorder;

    audioChunksRef.current = [];

    recorder.ondataavailable = (event) => {
      audioChunksRef.current.push(event.data);
    };

    recorder.start();

    setIsRecording(true);

    toast("info", "Recording started");
  } catch (err) {
    toast("error", "Microphone permission denied");
  }
};

const stopRecording = async () => {
  const recorder = mediaRecorderRef.current;

  if (!recorder) return;

  recorder.stop();

  recorder.onstop = async () => {
    try {
      const audioBlob = new Blob(
        audioChunksRef.current,
        { type: "audio/webm" }
      );

      const formData = new FormData();

      formData.append(
  "voiceFile",
  audioBlob,
  "speech.webm"
);

      setIsTranscribing(true);

      const response = await fetch(
  CONTENT_SUBMIT_API,
  {
    method: "POST",
    body: formData,
  }
);

const data = await response.json();

const voiceText =
  data?.data?.rawInstruction ||
  data?.data?.generatedContent ||
  data?.data?.editedContent ||
  "";

if (voiceText) {
  setRawInstruction((prev) =>
    `${prev} ${voiceText}`.trim()
  );

  if (!submitPlatform) {
    setSubmitAttempted(true);

    toast(
      "warning",
      "Voice converted successfully. Please select a platform before submitting."
    );
  }
}

      toast("success", "Voice converted");
      setIsTranscribing(false);
    } catch (error) {
  console.error(error);
  setIsTranscribing(false);
  toast("error", "Voice conversion failed");
}
  };

  streamRef.current?.getTracks().forEach(
    (track) => track.stop()
  );

  setIsRecording(false);
};

  const handleLogin = () => {
    if (username === STATIC_USERNAME && password === STATIC_PASSWORD) {
      sessionStorage.setItem("radhAIAdminLogin", "true");
      setIsLoggedIn(true);
      setLoginError("");
      toast("success", "Login successful");
    } else {
      setLoginError("Invalid username or password");
      toast("error", "Invalid username or password");
    }
  };

  const handleLogout = () => {
    stopRecording();
    sessionStorage.removeItem("radhAIAdminLogin");
    setIsLoggedIn(false);
    setUsername("");
    setPassword("");
    toast("info", "Logged out");
  };

  
  const addCompanyFiles = (files: FileList | null) => {
    if (!files?.length) return;
    setCompanyFileAttempted(true);
    const selected = Array.from(files);


    setCompanyFiles((prev) => {
      const existing = new Set(prev.map((f) => `${f.name}-${f.size}`));
      return [...prev, ...selected.filter((f) => !existing.has(`${f.name}-${f.size}`))];
    });
  };

  const addAttachments = (files: FileList | null) => {
  if (!files?.length) return;
    setSubmitContentAttempted(true);
    const selected = Array.from(files);
    setAttachments((prev) => {
      const existing = new Set(prev.map((f) => `${f.name}-${f.size}`));
      return [...prev, ...selected.filter((f) => !existing.has(`${f.name}-${f.size}`))];
    });
  };

  const removeCompanyFile = (index: number) => setCompanyFiles((prev) => prev.filter((_, i) => i !== index));
  const removeAttachment = (index: number) => setAttachments((prev) => prev.filter((_, i) => i !== index));

  const handleCompanyFileUpload = async () => {
    if (!companyPlatform) { toast("warning", "Please select platform"); return; }
   if (
  companyPlatform === "OTHER" &&
  !customCompanyPlatformName.trim()
){
  toast(
    "warning",
    "Please enter a custom platform name."
  );
  return;
}
    if (!companyFiles.length) { toast("warning", "Choose company files"); return; }
    try {
      setIsCompanyUploading(true);
      for (const file of companyFiles) {
        const params = new URLSearchParams();
params.append("platformType", companyPlatform);

if (
  companyPlatform === "OTHER" &&
  customCompanyPlatformName.trim()
) {
  params.append(
    "customPlatformName",
    customCompanyPlatformName.trim()
  );
}        const formData = new FormData();
        formData.append("file", file);
        const res = await fetch(`${COMPANY_UPLOAD_API}?${params.toString()}`, { method: "POST", body: formData });
        let data: any = null;
        try { data = await res.json(); } catch { data = null; }
        if (!res.ok || data?.success === false) throw new Error(data?.message || `Upload failed: ${file.name}`);
      }
      setCompanyFiles([]);
setShowCompanyUploadSuccess(true);
    } catch (error: any) {
      toast("error", error?.message || "Upload failed");
    } finally {
      setIsCompanyUploading(false);
    }
  };

 const handleSubmitContent = async () => {
  const instruction = rawInstruction.trim();
  setSubmitAttempted(true);

  if (!submitPlatform) {
    toast("warning", "Please select platform");
    return;
  }

  if (
    submitPlatform === "OTHER" &&
    !customPlatformName.trim()
  ) {
    toast(
      "warning",
      "Please enter a custom platform name."
    );
    return;
  }

  if (!instruction && !attachments.length) { toast("warning", "Add text or attach files"); return; }
  try {
   if (isRecording) {
  toast("warning", "Please stop recording first");
  return;
}

setIsSubmitting(true);
      setIsEditing(false);
      setContentId("");
      setGeneratedContext("");
      setGeneratedContent("");
      setEditedContent("");
      setAddedToClone(false);
      setShowBlogFlow(false);
      setBlogPreview(null);
      setPublishedBlog(null);
     const params = new URLSearchParams();

params.append("rawInstruction", instruction);
params.append("platform", submitPlatform);

if (
  submitPlatform === "OTHER" &&
  customPlatformName.trim()
) {
  params.append(
    "customPlatformName",
    customPlatformName.trim()
  );
}

const formData = new FormData();

attachments.forEach((file) =>
  formData.append("attachment", file)
);

const res = await fetch(
  `${CONTENT_SUBMIT_API}?${params.toString()}`,
  {
    method: "POST",
    body: formData,
  }
);
      const data: SubmitResponse = await res.json();
      if (!res.ok || data.success === false) { toast("error", data.message || "Something went wrong"); return; }
      const output = data.data?.editedContent || data.data?.generatedContent || data.data?.rawContent || "";
      const context = data.data?.context || data.data?.generatedContext || output;
      setContentId(data.data?.contentId || "");
      setGeneratedContext(context);
      setGeneratedContent(output);
      setEditedContent(output);
      toast("success", "Content generated successfully");
    } catch (err) {
      toast("error", "Cannot connect to server");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddToClone = async () => {
    if (!contentId || !generatedContent.trim()) { toast("warning", "Generate content first"); return; }
    setConfirmModal({
      open: true,
      title: "Add to Clone?",
      description: "The edited content will be sent to the clone knowledge flow. This action will train the radhAI clone with this content.",
      confirmLabel: "Yes, Add to Clone",
      confirmColor: "green",
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          setIsApproving(true);
          const payload = {
            entityId: contentId,
            entityType: "CONTENT",
            editedContent: currentContentForClone(),
            confirmed: true,
          };
          await fetchJson(ADD_TO_CLONE_API, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          });
          setAddedToClone(true);
          setShowBlogFlow(true);
          toast("success", "Added to clone successfully");
        } catch (error: any) {
          toast("error", error?.message || "Add to clone failed");
        } finally {
          setIsApproving(false);
        }
      },
    });
  };

  const handleRejectContent = async () => {
    setConfirmModal({
      open: true,
      title: "Clear Generated Content?",
      description: "This will remove the current output from the screen. You can re-generate by submitting again.",
      confirmLabel: "Clear Content",
      confirmColor: "red",
      onConfirm: () => { setConfirmModal(null); handleClear(); },
    });
  };

  const handleCreateImage = async () => {
    if (!contentId) return toast("warning", "Content ID missing");
    try {
      setImageLoading(true);
      const data = await fetchJson(`${IMAGE_API}/${encodeURIComponent(contentId)}?entityType=CONTENT`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ entityId: contentId, entityType: "CONTENT" }),
      });
      const imageData = data?.data || {};
      const nextImageUrl = imageData?.imageUrl || imageData?.url || imageData?.generatedImageUrl || "";
      setBlogPreview((prev) => mergeBlogPreview({ ...(imageData || {}), imageUrl: nextImageUrl || prev?.imageUrl || "" }, prev));
      setPublishedBlog(null);
      toast("success", "Image generated");
    } catch (error: any) {
      toast("error", error?.message || "Image generation failed");
    } finally {
      setImageLoading(false);
    }
  };

  

  const handleFormatBlog = async (generateImage = false) => {
  if (!contentId) {
    return toast("warning", "Content ID missing");
  }

  try {
   setBlogFormatting(true);
setFormatLoading(true);
setShowBlogFlow(true);
setIsBlogModalOpen(true);
setBlogPreview(null);       // EMPTY PREVIEW FIRST

    const data = await fetchJson(BLOG_FORMAT_API, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        entityId: contentId,
        entityType: "CONTENT",
        generateImage,
      }),
    });

    const formattedData = data?.data || {};

    setBlogPreview((prev) =>
      mergeBlogPreview(
        {
          ...(formattedData || {}),
          imageUrl:
            formattedData?.imageUrl ||
            prev?.imageUrl ||
            "",
        },
        prev
      )
    );

    setPublishedBlog(null);

    toast("success", "Blog preview ready");
  } catch (error: any) {
    toast(
      "error",
      error?.message || "Blog preview failed"
    );
  } finally {
    setBlogFormatting(false);     // STOP ANIMATION
    setFormatLoading(false);
  }
};

  const handlePublishBlog = async () => {
    if (!blogPreview) return toast("warning", "Generate blog preview first");
    try {
      setPublishLoading(true);
      const payload: BlogPayload = {
        ...blogPreview,
        entityId: getEntityId(blogPreview) || contentId,
        entityType: "CONTENT",
       imageUrl: blogPreview.imageUrl || "",
videoUrl: blogPreview.videoUrl || "",
videoFileUrl: blogPreview.videoFileUrl || null,
        status: "PUBLISHED",
      };
      const data = await fetchJson(BLOG_PUBLISH_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const publishedData = data?.data || payload;
      setPublishedBlog(publishedData);
      setBlogPreview(null);
      setPublishSuccessModal(true);
    } catch (error: any) {
      toast("error", error?.message || "Blog publish failed");
    } finally {
      setPublishLoading(false);
    }
  };

  const handleStartEdit = () => {
    setParsedEdits(parsedGeneratedContent ? JSON.parse(JSON.stringify(parsedGeneratedContent)) : null);
    setIsEditing(true);
  };

  const handleSaveEdits = () => {
    if (parsedEdits) {
      const serialized = JSON.stringify(parsedEdits);
      setGeneratedContent(serialized);
      setEditedContent(serialized);
    } else {
      setGeneratedContent(editedContent);
    }
    setIsEditing(false);
    toast("success", "Changes saved");
  };

  const handleCancelEdit = () => { setParsedEdits(null); setIsEditing(false); };
const handleClearInput = () => {
  setRawInstruction("");
  setInterimVoiceText("");

  toast("success", "Input cleared");
};
  const handleClear = () => {
    stopRecording();
    setRawInstruction("");
    setInterimVoiceText("");
    setAttachments([]);
    setGeneratedContent("");
    setEditedContent("");
    setGeneratedContext("");
    setContentId("");
    setIsEditing(false);
    setParsedEdits(null);
    setAddedToClone(false);
    setShowBlogFlow(false);
setBlogPreview(null);
setIsBlogModalOpen(false);
    setPublishedBlog(null);
    setSubmitAttempted(false);
    setCompanyFileAttempted(false);
    setSubmitContentAttempted(false);
  };
  const handleRefreshRadhAI = () => {
  handleClear();

  setCompanyFiles([]);
  setCompanyPlatform("");
  setSubmitPlatform("");

  setCustomPlatformName("");
  setCustomCompanyPlatformName("");

  toast("success", " refreshed sucessfully");
};

  if (!isLoggedIn) {
    return (
      <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[#070A16] px-4 text-white">
        <ToastContainer />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_18%_20%,rgba(46,229,255,0.14),transparent_32%),radial-gradient(circle_at_82%_18%,rgba(143,116,255,0.16),transparent_30%),radial-gradient(circle_at_50%_92%,rgba(174,244,91,0.10),transparent_38%)]" />
        <div className="absolute inset-0 opacity-[0.08] bg-[linear-gradient(to_right,#5EDDF2_1px,transparent_1px),linear-gradient(to_bottom,#5EDDF2_1px,transparent_1px)] bg-[size:44px_44px]" />
        <motion.div
          initial={{ opacity: 0, y: 24, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          className="relative w-full max-w-[410px] rounded-[30px] border border-[#3A465B]/70 bg-[#1C2433]/80 p-5 shadow-[0_30px_90px_rgba(0,0,0,.52)] backdrop-blur-2xl"
        >
          <div className="mb-6 text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-[#B6F269] via-[#75E6C9] to-[#5EDDF2] text-black">
              <Lock size={24} />
            </div>
            <h1 className="text-xl font-black">radhAI Admin</h1>
            <p className="mt-1 text-xs text-[#9AA7BC]">Secure content training and approval panel</p>
          </div>
          <div className="space-y-3">
            <div className="relative">
              <User className="absolute left-4 top-3 text-[#7DEBFF]" size={17} />
              <input
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Username"
                className="h-11 w-full rounded-xl border border-[#3A465B]/70 bg-[#212B3B]/80 pl-11 pr-4 text-sm text-white outline-none placeholder:text-[#78859A]"
              />
            </div>
            <div className="relative">
              <Lock className="absolute left-4 top-3 text-[#7DEBFF]" size={17} />
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                onKeyDown={(e) => e.key === "Enter" && handleLogin()}
                className="h-11 w-full rounded-xl border border-[#3A465B]/70 bg-[#212B3B]/80 pl-11 pr-11 text-sm text-white outline-none placeholder:text-[#78859A]"
              />
              <button onClick={() => setShowPassword((prev) => !prev)} className="absolute right-4 top-3 text-[#C4CEDD]" type="button">
                {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
              </button>
            </div>
            <AnimatePresence>
              {loginError && <InlineBanner variant="error" message={loginError} />}
            </AnimatePresence>
            <motion.button
              {...buttonMotion}
              onClick={handleLogin}
              className="h-11 w-full rounded-xl bg-gradient-to-br from-[#B6F269] via-[#75E6C9] to-[#5EDDF2] text-sm font-black text-black shadow-[0_16px_40px_rgba(94,221,242,0.22)]"
            >
              Login
            </motion.button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen overflow-x-hidden bg-[#070A16] text-white">
      <ToastContainer />

      {/* Centered Confirm Modal */}
      {confirmModal && (
        <ConfirmModal
          open={confirmModal.open}
          title={confirmModal.title}
          description={confirmModal.description}
          confirmLabel={confirmModal.confirmLabel}
          confirmColor={confirmModal.confirmColor}
          onConfirm={confirmModal.onConfirm}
          onCancel={() => setConfirmModal(null)}
        />
      )}

      {/* Publish Success Modal */}
      <PublishSuccessModal
        open={publishSuccessModal}
        blogPostId={publishedBlog?.blogPostId}
        onClose={() => setPublishSuccessModal(false)}
        onNewContent={() => { setPublishSuccessModal(false); handleClear(); }}
      />

      <AnimatePresence>
  {showCompanyUploadSuccess && (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[300] flex items-center justify-center bg-black/70 backdrop-blur-md"
    >
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.8, opacity: 0 }}
        className="w-full max-w-md rounded-3xl border border-lime-400/20 bg-[#111A2B] p-8 text-center shadow-[0_40px_120px_rgba(0,0,0,0.7)]"
      >
        <div className="mx-auto mb-5 flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-[#B6F269] via-[#75E6C9] to-[#5EDDF2]">
          <CheckCircle size={40} className="text-black" />
        </div>

        <h3 className="text-2xl font-black text-white">
          Upload Successful
        </h3>

        <p className="mt-3 text-sm text-[#9AA7BC]">
          Company knowledge files have been successfully added to the Knowledge Base.
        </p>

        <button
          onClick={() => setShowCompanyUploadSuccess(false)}
          className="mt-6 h-11 w-full rounded-2xl bg-gradient-to-br from-[#B6F269] via-[#75E6C9] to-[#5EDDF2] text-sm font-black text-black"
        >
          OK
        </button>
      </motion.div>
    </motion.div>
  )}
</AnimatePresence>

      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_15%_15%,rgba(46,229,255,0.12),transparent_30%),radial-gradient(circle_at_85%_18%,rgba(143,116,255,0.13),transparent_32%),radial-gradient(circle_at_50%_95%,rgba(174,244,91,0.08),transparent_34%)]" />
      <div className="pointer-events-none absolute inset-0 opacity-[0.06] bg-[linear-gradient(to_right,#5EDDF2_1px,transparent_1px),linear-gradient(to_bottom,#5EDDF2_1px,transparent_1px)] bg-[size:40px_40px]" />

      <main className="relative z-10 mx-auto w-full max-w-[1500px] px-3 py-4 sm:px-5 lg:px-6">
        {/* Company Upload Section */}
        <motion.section
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-4 rounded-2xl border border-[#3A465B]/70 bg-[#1C2433]/82 p-3 shadow-[0_18px_60px_rgba(0,0,0,.32)] backdrop-blur-2xl sm:p-4"
        >
         <div className="mb-3 flex items-center justify-between">
  <div className="flex items-center gap-2">
    <Upload size={18} className="text-[#7DEBFF]" />
    <h2 className="text-sm font-black">
      Upload Company Knowledge File
    </h2>
  </div>

  <button
    type="button"
    onClick={handleRefreshRadhAI}
    className="flex h-9 items-center gap-2 rounded-full border border-[#5EDDF2]/30 bg-[#5EDDF2]/10 px-4 text-xs font-bold text-[#DDFBFF]"
  >
    <RotateCcw size={14} />
    Refresh
  </button>
</div>
          <PlatformPicker
            label="Please Select Platform"
            value={companyPlatform}
            onChange={(v) => { setCompanyPlatform(v); setCompanyFileAttempted(false); }}
          />
          {companyPlatform === "OTHER" && (
            
  <div className="mb-3">
    <input
      type="text"
      value={customCompanyPlatformName}
      onChange={(e) =>
        setCustomCompanyPlatformName(e.target.value)
      }
      placeholder="Enter custom company platform"
      className="h-11 w-full rounded-xl border border-[#3A465B]/70 bg-[#212B3B]/80 px-4 text-sm text-white outline-none"
    />
  </div>
)}
          {companyPlatform === "OTHER" &&
 !customCompanyPlatformName.trim() &&
 companyFileAttempted && (
  <p className="mt-2 text-xs text-red-400">
    Custom platform name is required.
  </p>
)}
          
          <AnimatePresence>
            {companyFileAttempted && !companyPlatform && companyFiles.length > 0 && (
              <div className="mb-3">
                <InlineBanner variant="warning" message="Please select a Platform before uploading files." />
              </div>
            )}
          </AnimatePresence>
         <div className="grid gap-2 sm:grid-cols-[1fr_120px]">
  <label className="flex h-11 cursor-pointer items-center gap-2 rounded-xl border border-dashed border-[#5EDDF2]/30 bg-[#212B3B]/80 px-3 text-xs font-semibold">
    <FileText size={15} className="text-[#7DEBFF]" />

    <div className="flex flex-col overflow-hidden">
      <span className="truncate">
        {companyFiles.length
          ? `${companyFiles.length} file(s) selected`
          : "Choose company files"}
      </span>

      
    </div>

    <input
      type="file"
      multiple
      className="hidden"
      onChange={(e) => {
        const files = Array.from(
          e.target.files || []
        );

        const oversized = files.filter(
          (file) =>
            file.size > 500 * 1024 * 1024
        );

        if (oversized.length > 0) {
          toast(
            "error",
            "Maximum company file size is 500 MB"
          );
          e.target.value = "";
          return;
        }

        addCompanyFiles(e.target.files);
        e.target.value = "";
      }}
    />
  </label>

  <ActionButton
    onClick={handleCompanyFileUpload}
    disabled={!canUploadCompanyFiles}
    label={
      isCompanyUploading
        ? "Uploading"
        : "Upload"
    }
  />
</div>

<p className="mt-2 text-[11px] text-[#9AA7BC]">
  Supported file size up to
  <span className="ml-1 font-bold text-[#7DEBFF]">
    500 MB
  </span>
</p>
          {companyFiles.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {companyFiles.map((file, index) => (
                <FileChip key={`${file.name}-${file.size}-${index}`} icon={<FileText size={13} />} name={file.name} onRemove={() => removeCompanyFile(index)} />
              ))}
              <button type="button" onClick={() => setCompanyFiles([])} className="rounded-full border border-red-300/25 bg-red-500/10 px-3 py-2 text-[11px] font-bold text-red-200">
                Clear all
              </button>
            </div>
          )}
        </motion.section>

        <section className="grid gap-4 lg:grid-cols-[minmax(0,0.92fr)_minmax(0,1.08fr)]">
          {/* LEFT: Input + Blog Workflow */}
          <div className="space-y-3">
            <GlassCard>
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
               <div className="flex items-center gap-2">
  <Sparkles size={18} className="text-[#7DEBFF]" />
  <h2 className="text-sm font-black sm:text-base">
    Radha's Input
  </h2>

 
</div>
                <div className="flex items-center gap-2">
                  {/* Added to Clone Badge */}
                  <AnimatePresence>
                    {addedToClone && (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.8, x: 10 }}
                        animate={{ opacity: 1, scale: 1, x: 0 }}
                        exit={{ opacity: 0, scale: 0.8 }}
                        className="flex items-center gap-1.5 rounded-full border border-lime-400/30 bg-lime-400/15 px-3 py-1.5 text-[11px] font-black text-lime-200 shadow-[0_0_18px_rgba(182,242,105,0.18)]"
                      >
                        <BadgeCheck size={13} className="text-lime-300" />
                        Added to Clone
                      </motion.div>
                    )}
                  </AnimatePresence>
                  <button
                    type="button"
                    onClick={() => setIsInputExpanded((prev) => !prev)}
                    className="inline-flex h-9 items-center justify-center rounded-full border border-[#5EDDF2]/25 bg-[#5EDDF2]/10 px-3 text-[11px] font-black text-[#DDFBFF] hover:border-[#5EDDF2]/50"
                  >
                    {isInputExpanded ? "Collapse Input" : "Expand Input"}
                  </button>
                </div>
              </div>

              <PlatformPicker
                label="Please Select Platform"
                value={submitPlatform}
                onChange={(v) => { setSubmitPlatform(v); setSubmitAttempted(false); setSubmitContentAttempted(false); }}
              />
              {submitPlatform === "OTHER" && (
  <div className="mb-3">
    <input
      type="text"
      value={customPlatformName}
      onChange={(e) =>
        setCustomPlatformName(e.target.value)
      }
      placeholder="Enter custom platform name"
      className="h-11 w-full rounded-xl border border-[#3A465B]/70 bg-[#212B3B]/80 px-4 text-sm text-white outline-none"
    />
  </div>
)}
            {submitPlatform === "OTHER" &&
 !customPlatformName.trim() &&
 submitAttempted && (
  <p className="mt-2 text-xs text-red-400">
    Custom platform name is required.
  </p>
)}

              <AnimatePresence>
                {submitContentAttempted && !submitPlatform && hasInputContent && (
                  <div className="mb-3">
                    <InlineBanner variant="warning" message="Please select a Platform before submitting content." />
                  </div>
                )}
              </AnimatePresence>

           <div className="rounded-[24px] border border-[#3A465B]/70 bg-[#0B1020]/65 p-3">
  <div className="relative">

    <textarea
      value={displayText}
      onChange={(e) => {
        setInterimVoiceText("");
        setRawInstruction(e.target.value);
        if (!submitPlatform) setSubmitContentAttempted(true);
      }}
      placeholder="Type content here or use the mic button..."
      className={`w-full resize-none bg-transparent p-2 text-[13px] leading-6 text-white outline-none ${
        isInputExpanded
          ? "min-h-[520px] sm:min-h-[620px] lg:min-h-[720px]"
          : "min-h-[120px] sm:min-h-[180px] lg:min-h-[200px]"
      }`}
    />

    {isRecording && (
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
        <div className="flex items-center gap-[2px]">
          {[
            6,10,14,18,22,18,14,10,
            6,10,14,18,22,18,14,10,
            6,10,14,18,22,18,14,10,
            6,10,14,18,22,18,14,10
          ].map((height, i) => (
            <div
              key={i}
              className="w-[3px] rounded-full bg-cyan-400 animate-pulse"
              style={{
                height: `${height}px`,
                animationDelay: `${i * 0.05}s`,
              }}
            />
          ))}
        </div>
      </div>
    )}

 {isTranscribing && (
    <div className="absolute inset-0 flex flex-col items-center justify-center">
      <div className="relative mb-4 flex h-16 w-16 items-center justify-center">
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            className="absolute inset-0 rounded-full border border-cyan-400/30"
            style={{
              animation: `ping 2s ${i * 0.4}s infinite`,
            }}
          />
        ))}

        <Brain
          size={24}
          className="animate-pulse text-cyan-400"
        />
      </div>

      <p className="text-sm font-bold text-cyan-300">
        AI Processing
      </p>

      <p className="mt-1 text-xs text-slate-400">
        Converting voice to text...
      </p>

      <div className="mt-3 flex gap-1">
        {[0,1,2].map((i) => (
          <span
            key={i}
            className="h-2 w-2 rounded-full bg-cyan-400 animate-bounce"
            style={{
              animationDelay: `${i * 0.2}s`,
            }}
          />
        ))}
      </div>
    </div>
  )}

  </div>

                   {/* {hasInputContent && !submitPlatform && (
  <div className="mb-3">
    <InlineBanner
      variant="warning"
      message="Please select a platform before submitting content."
    />
  </div>
)} */}
                <div className="flex flex-wrap items-center gap-2 border-t border-[#303A4E]/70 pt-3">
                 <label className="flex h-9 cursor-pointer items-center gap-2 rounded-full border border-[#3A465B]/70 bg-[#212B3B]/80 px-3 text-xs font-bold">
  <Paperclip size={14} />

  <div className="flex flex-col leading-tight">
    <span>Attach Files</span>
    <span className="text-[9px] text-[#7DEBFF]">
      Max 100 MB
    </span>
  </div>

  <input
    type="file"
    multiple
    className="hidden"
    onChange={(e) => {
      const files = Array.from(
        e.target.files || []
      );

      const oversized = files.filter(
        (file) =>
          file.size > 100 * 1024 * 1024
      );

      if (oversized.length > 0) {
        toast(
          "error",
          "Maximum attachment size is 100 MB"
        );
        e.target.value = "";
        return;
      }

      addAttachments(e.target.files);
      e.target.value = "";
    }}
  />
</label>
                  <motion.button
                    {...buttonMotion}
                    type="button"
onClick={isRecording ? stopRecording : startRecording}
                    className={`flex h-9 items-center gap-1.5 rounded-full px-3 text-xs font-bold ${
  isRecording
    ? "bg-red-500 text-white"
    : isTranscribing
    ? "bg-yellow-500 text-black"
    : "border border-[#5EDDF2]/30 bg-[#5EDDF2]/10 text-[#DDFBFF]"
}`}
                  >
                    {isRecording ? (
  <>
    <Square size={14} />
    <span className="animate-pulse h-2 w-2 rounded-full bg-red-400" />
    Stop..
  </>
) : isTranscribing ? (
  <>
    <Loader2 size={14} className="animate-spin" />
    Processing...
  </>
) : (
  <>
    <Mic size={14} />
     Voice 
  </>
)}
                  </motion.button>
                 <motion.button
  {...buttonMotion}
  type="button"
  onClick={handleClearInput}
  className="flex h-9 items-center gap-1.5 rounded-full border border-[#3A465B]/70 bg-[#212B3B]/80 px-3 text-xs font-bold"
>
  <RotateCcw size={14} />
  Clear
</motion.button>
                  <motion.button
                    {...buttonMotion}
                    type="button"
                    onClick={handleSubmitContent}
                    disabled={!canSubmit}
                    className="ml-auto flex h-9 items-center gap-1.5 rounded-full bg-gradient-to-br from-[#B6F269] via-[#75E6C9] to-[#5EDDF2] px-4 text-xs font-black text-black shadow-[0_14px_32px_rgba(94,221,242,0.20)] disabled:cursor-not-allowed disabled:opacity-40 max-sm:ml-0 max-sm:w-full max-sm:justify-center"
                  >
                    {isSubmitting ? "Generating" : "Submit"}
                    <Send size={14} />
                  </motion.button>
                </div>
              </div>

              <div className="mt-3 space-y-2">
                <AnimatePresence>
                  
                  {submitAttempted && submitPlatform && !hasInputContent && (
                    <InlineBanner key="no-content" variant="warning" message="Add raw instruction text or attach at least one file." />
                  )}
        {/* {isRecording && (
  <div className="flex justify-center py-4">
<div className="flex items-center gap-[2px] h-8">
        {[
  6,10,14,18,22,18,14,10,
  6,10,14,18,22,18,14,10,
  6,10,14,18,22,18,14,10,
  6,10,14,18,22,18,14,10,
  6,10,14,18,22,18,14,10
].map(
        (height, i) => (
          <div
            key={i}
            className="w-1 rounded-full bg-cyan-400 animate-pulse"
            style={{
              height: `${height}px`,
              animationDelay: `${i * 0.08}s`,
            }}
          />
        )
      )}
    </div>
  </div>
)} */}

{/* {isTranscribing && (
  <InlineBanner
    key="transcribing"
    variant="info"
    message="Processing voice ....."
  />
)} */}

                
                </AnimatePresence>
              </div>

              {attachments.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {attachments.map((file, index) => (
                    <FileChip key={`${file.name}-${file.size}-${index}`} icon={<Paperclip size={13} />} name={file.name} onRemove={() => removeAttachment(index)} />
                  ))}
                  <button type="button" onClick={() => setAttachments([])} className="rounded-full border border-red-300/25 bg-red-500/10 px-3 py-2 text-[11px] font-bold text-red-200">
                    Clear files
                  </button>
                </div>
              )}
            </GlassCard>

            {generatedContent && !isSubmitting && (
            <ContentBlogWorkflow
  showBlogFlow={showBlogFlow}
  addedToClone={addedToClone}
  blogPreview={blogPreview}
  blogFormatting={blogFormatting}
  publishedBlog={publishedBlog}
  imageLoading={imageLoading}
  formatLoading={formatLoading}
  publishLoading={publishLoading}
  canPublishBlog={canPublishBlog}
  hasFormattedBlogPreview={hasFormattedBlogPreview}
  isBlogModalOpen={isBlogModalOpen}
  onSetBlogModalOpen={setIsBlogModalOpen}
  onShowBlogFlow={() => setShowBlogFlow(true)}
  onCreateImage={handleCreateImage}
  onFormatBlog={() => handleFormatBlog(false)}
  onPublishBlog={handlePublishBlog}
  onChangePreview={setBlogPreview}
  onReset={handleClear}
/>
            )}
          </div>

          {/* RIGHT: Generated Output */}
          <GlassCard>
            <div ref={generatedOutputRef} className="scroll-mt-4">
              <div className="mb-3 flex items-center gap-2">
                <CheckCircle size={18} className="text-[#B6F269]" />
                <h2 className="text-sm font-black sm:text-base">Generated Output</h2>
              </div>

              <AnimatePresence mode="wait">
                {isSubmitting ? (
                  <AIReasoningLoader />
                ) : !generatedContent ? (
                  <motion.div
                    key="empty"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="flex min-h-[300px] items-center justify-center rounded-2xl border border-dashed border-[#3A465B]/70 bg-[#0B1020]/55 p-4 text-center text-[13px] text-[#9AA7BC] sm:min-h-[380px]"
                  >
                    <div>
                      <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl border border-[#303A4E]/70 bg-[#1B2432]/75">
                        <Sparkles size={22} className="text-[#7DEBFF]" />
                      </div>
                      Generated content will appear here after submit.
                      <p className="mt-1 text-xs text-[#78859A]">
                        Approve button will be enabled only after content is generated.
                      </p>
                    </div>
                  </motion.div>
                ) : (
                  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
                    <div className="rounded-[24px] border border-[#3A465B]/70 bg-[#0B1020]/65 p-3 sm:p-4">
                      <div className="mb-3 border-b border-[#303A4E]/70 pb-3">
                        <div className="grid gap-3">
                          <div className="min-w-0">
                            <p className="flex items-center gap-2 text-sm font-black text-[#7DEBFF]">
                              <Layers size={15} />
                              <span className="truncate">Polished Generated Content</span>
                            </p>
                            <p className="mt-1 text-[11px] text-[#9AA7BC]">
                              Review, edit and add this generated content to clone.
                            </p>
                          </div>
                          <div className="grid grid-cols-3 gap-2 sm:flex sm:flex-nowrap">
                            <motion.button
                              {...buttonMotion}
                              type="button"
                              onClick={handleAddToClone}
                              disabled={!canAddToClone || addedToClone}
                              className="flex h-9 min-w-0 items-center justify-center gap-1.5 rounded-xl bg-gradient-to-br from-[#B6F269] via-[#75E6C9] to-[#5EDDF2] px-2 text-[11px] font-black text-black shadow-[0_12px_30px_rgba(34,211,238,0.2)] disabled:cursor-not-allowed disabled:opacity-40 sm:px-4 sm:text-xs"
                            >
                              <CheckCircle size={14} />
                              <span className="truncate">
                                {isApproving ? "Adding..." : addedToClone ? "Added ✓" : "Add to Clone"}
                              </span>
                            </motion.button>
                            <motion.button
                              {...buttonMotion}
                              type="button"
                              onClick={handleRejectContent}
                              disabled={!canAddToClone}
                              className="flex h-9 min-w-0 items-center justify-center gap-1.5 rounded-xl border border-red-300/30 bg-red-500/10 px-2 text-[11px] font-bold text-red-200 disabled:cursor-not-allowed disabled:opacity-40 sm:px-4 sm:text-xs"
                            >
                              <X size={14} />
                              <span className="truncate">Clear</span>
                            </motion.button>
                            {!isEditing ? (
                              <motion.button
                                {...buttonMotion}
                                type="button"
                                onClick={handleStartEdit}
                                className="flex h-9 min-w-0 items-center justify-center gap-1.5 rounded-xl border border-[#3A465B]/70 bg-[#212B3B]/80 px-2 text-[11px] font-bold sm:px-4 sm:text-xs"
                              >
                                <Edit3 size={13} />
                                <span className="truncate">Edit</span>
                              </motion.button>
                            ) : (
                              <div className="col-span-3 grid grid-cols-2 gap-2 sm:col-span-1 sm:flex">
                                <motion.button
                                  {...buttonMotion}
                                  type="button"
                                  onClick={handleSaveEdits}
                                  className="flex h-9 items-center justify-center gap-1.5 rounded-xl bg-gradient-to-br from-[#B6F269] via-[#75E6C9] to-[#5EDDF2] px-3 text-[11px] font-black text-black sm:text-xs"
                                >
                                  <Save size={13} />
                                  Save
                                </motion.button>
                                <motion.button
                                  {...buttonMotion}
                                  type="button"
                                  onClick={handleCancelEdit}
                                  className="flex h-9 items-center justify-center gap-1.5 rounded-xl border border-[#3A465B]/70 bg-[#212B3B]/80 px-3 text-[11px] font-bold sm:text-xs"
                                >
                                  <X size={13} />
                                  Cancel
                                </motion.button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {!isEditing ? (
                        <div className="max-h-[460px] overflow-y-auto pr-1">
                          {parsedGeneratedContent ? (
                            <PolishedGeneratedView data={parsedGeneratedContent} />
                          ) : (
                            <div className="whitespace-pre-wrap text-sm leading-7 text-[#E5EAF2]">
                              {generatedContent}
                            </div>
                          )}
                        </div>
                      ) : parsedEdits ? (
                        <ParsedEditView data={parsedEdits} onChange={setParsedEdits} />
                      ) : (
                        <textarea
                          value={editedContent}
                          onChange={(e) => setEditedContent(e.target.value)}
                          className="min-h-[320px] w-full resize-none bg-transparent text-[13px] leading-6 text-white outline-none"
                        />
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </GlassCard>
        </section>
      </main>
    </div>
  );
}

// ─── Platform Picker ───────────────────────────────────────────────────────
function PlatformPicker({ value, onChange, label }: { value: string; onChange: (v: string) => void; label?: string }) {
  return (
    <div className="mb-3">
      {label && <p className="mb-2 text-[10px] font-bold uppercase tracking-[0.16em] text-[#9AA7BC]">{label}</p>}
      <div className="flex flex-wrap gap-2">
        {PLATFORMS.map((p) => {
          const active = value === p.key;
          return (
            <motion.button
              key={p.key}
              type="button"
              whileHover={{ y: -1, scale: 1.03 }}
              whileTap={{ scale: 0.96 }}
              onClick={() => onChange(active ? "" : p.key)}
              className={`relative flex min-h-8 items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-bold transition-colors ${
                active
                  ? "bg-gradient-to-r from-[#5EDDF2] to-[#B6F269] text-black shadow-[0_8px_22px_rgba(94,221,242,0.26)]"
                  : "border border-[#3A465B]/70 bg-[#1B2432]/75 text-[#C4CEDD] hover:border-[#5EDDF2]/40 hover:text-white"
              }`}
            >
              {p.label}
              {active && <X size={12} className="relative shrink-0" />}
            </motion.button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Inline Banner ─────────────────────────────────────────────────────────
const BANNER_VARIANTS = {
  warning: { icon: AlertTriangle, bar: "bg-yellow-400", border: "border-yellow-300/25", bg: "bg-yellow-400/8", icon_color: "text-yellow-300", text: "text-yellow-100", glow: "shadow-[0_0_18px_rgba(250,204,21,0.12)]" },
  error: { icon: AlertCircle, bar: "bg-red-400", border: "border-red-400/25", bg: "bg-red-500/8", icon_color: "text-red-300", text: "text-red-100", glow: "shadow-[0_0_18px_rgba(248,113,113,0.12)]" },
  info: { icon: Info, bar: "bg-[#5EDDF2]", border: "border-[#5EDDF2]/25", bg: "bg-[#5EDDF2]/8", icon_color: "text-[#5EDDF2]", text: "text-[#DDFBFF]", glow: "shadow-[0_0_18px_rgba(34,211,238,0.12)]" },
} as const;

function InlineBanner({ variant, message }: { variant: keyof typeof BANNER_VARIANTS; message: string }) {
  const v = BANNER_VARIANTS[variant];
  const Icon = v.icon;
  return (
    <motion.div
      initial={{ opacity: 0, y: -6, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -6, scale: 0.98 }}
      transition={{ duration: 0.22, ease: "easeOut" }}
      className={`relative flex items-start gap-3 overflow-hidden rounded-2xl border px-4 py-3 backdrop-blur-sm ${v.border} ${v.bg} ${v.glow}`}
    >
      <motion.span
        initial={{ scaleY: 0 }}
        animate={{ scaleY: 1 }}
        transition={{ duration: 0.28, ease: "easeOut" }}
        className={`absolute left-0 top-0 h-full w-[3px] origin-top rounded-r-full ${v.bar}`}
      />
      <span className="relative mt-[1px] shrink-0">
        <motion.span
          animate={{ scale: [1, 1.5, 1], opacity: [0.4, 0, 0.4] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          className={`absolute inset-0 rounded-full ${v.bar} opacity-30`}
        />
        <Icon size={15} className={v.icon_color} />
      </span>
      <p className={`text-xs font-semibold leading-5 ${v.text}`}>{message}</p>
    </motion.div>
  );
}

// ─── AI Reasoning Loader ───────────────────────────────────────────────────
const REASONING_STEPS = [
  { icon: Brain, label: "Parsing your instruction", color: "text-[#5EDDF2]" },
  { icon: Cpu, label: "Retrieving company knowledge", color: "text-[#B9A7FF]" },
  { icon: Sparkles, label: "Structuring content sections", color: "text-[#B6F269]" },
  { icon: Zap, label: "Polishing final output", color: "text-yellow-300" },
];

function AIReasoningLoader() {
  return (
    <motion.div
      key="reasoning"
      initial={{ opacity: 0, scale: 0.97 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.97 }}
      transition={{ duration: 0.3 }}
      className="flex min-h-[300px] flex-col items-center justify-center rounded-2xl border border-[#5EDDF2]/20 bg-[#0B1020]/70 p-5 sm:min-h-[380px] sm:p-8"
    >
      <div className="relative mb-8 flex h-24 w-24 items-center justify-center sm:h-28 sm:w-28">
        {[0, 1, 2].map((i) => (
          <motion.span key={i} className="absolute inset-0 rounded-full border border-[#5EDDF2]/30"
            animate={{ scale: [1, 1.18 + i * 0.12, 1], opacity: [0.6, 0.15, 0.6] }}
            transition={{ duration: 2.2, delay: i * 0.55, repeat: Infinity, ease: "easeInOut" }}
          />
        ))}
        <motion.span className="absolute inset-0 rounded-full border-2 border-transparent border-t-cyan-400"
          animate={{ rotate: 360 }} transition={{ duration: 1.4, repeat: Infinity, ease: "linear" }} />
        <motion.span className="absolute inset-2 rounded-full border-2 border-transparent border-b-[#B6F269]"
          animate={{ rotate: -360 }} transition={{ duration: 2, repeat: Infinity, ease: "linear" }} />
        <motion.div animate={{ scale: [1, 1.12, 1] }} transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
          className="relative flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-cyan-400/20 to-lime-400/20">
          <Brain size={22} className="text-[#7DEBFF]" />
        </motion.div>
      </div>
      <motion.p animate={{ opacity: [0.7, 1, 0.7] }} transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        className="mb-1 bg-gradient-to-r from-cyan-200 via-lime-200 to-cyan-300 bg-clip-text text-base font-black text-transparent sm:text-lg">
        AI Reasoning
      </motion.p>
      <p className="mb-8 text-xs text-[#78859A]">Processing your content&hellip;</p>
      <div className="w-full max-w-xs space-y-3 sm:max-w-sm">
        {REASONING_STEPS.map(({ icon: Icon, label, color }, i) => (
          <motion.div key={label} initial={{ opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }}
            transition={{ delay: i * 0.45, duration: 0.4 }}
            className="flex items-center gap-3 rounded-2xl border border-[#303A4E]/60 bg-[#1A2230]/70 px-4 py-3"
          >
            <motion.span animate={{ opacity: [0.5, 1, 0.5] }}
              transition={{ duration: 1.6, delay: i * 0.45, repeat: Infinity, ease: "easeInOut" }} className={color}>
              <Icon size={15} />
            </motion.span>
            <span className="flex-1 text-xs font-semibold text-[#C4CEDD]">{label}</span>
            <span className="flex gap-1">
              {[0, 1, 2].map((d) => (
                <motion.span key={d} className="h-1.5 w-1.5 rounded-full bg-[#5EDDF2]"
                  animate={{ opacity: [0.2, 1, 0.2], scale: [0.8, 1.2, 0.8] }}
                  transition={{ duration: 1.2, delay: i * 0.45 + d * 0.2, repeat: Infinity, ease: "easeInOut" }}
                />
              ))}
            </span>
          </motion.div>
        ))}
      </div>
      <div className="mt-8 h-1 w-full max-w-xs overflow-hidden rounded-full bg-[#1B2432]/75 sm:max-w-sm">
        <motion.div className="h-full rounded-full bg-gradient-to-r from-[#5EDDF2] via-[#B6F269] to-[#5EDDF2]"
          animate={{ x: ["-100%", "100%"] }} transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }} />
      </div>
    </motion.div>
  );
}

// ─── Parsed Edit View ──────────────────────────────────────────────────────
function ParsedEditView({ data, onChange }: { data: ParsedGeneratedContent; onChange: (updated: ParsedGeneratedContent) => void }) {
  const set = (key: keyof ParsedGeneratedContent, value: any) => onChange({ ...data, [key]: value });
  const setSection = (index: number, field: keyof GeneratedSection, value: string) => {
    const updated = (data.sections || []).map((s, i) => i === index ? { ...s, [field]: value } : s);
    onChange({ ...data, sections: updated });
  };
  const fieldClass = "w-full resize-none rounded-xl border border-[#303A4E]/70 bg-[#0B1020]/65 p-3 text-[13px] leading-6 text-white outline-none focus:border-[#5EDDF2]/40 placeholder:text-[#78859A]";
  const labelClass = "mb-1 block text-[11px] font-bold uppercase tracking-[0.18em] text-[#9AA7BC]";
  return (
    <div className="max-h-[460px] space-y-3 overflow-y-auto pr-1">
      {data.title !== undefined && (
        <div><label className={labelClass}>Title</label><textarea rows={2} value={data.title || ""} onChange={(e) => set("title", e.target.value)} className={fieldClass} /></div>
      )}
      {data.intro !== undefined && (
        <div><label className={labelClass}>Introduction</label><textarea rows={3} value={data.intro || ""} onChange={(e) => set("intro", e.target.value)} className={fieldClass} /></div>
      )}
      {data.sections && data.sections.length > 0 && (
        <div className="space-y-3">
          <p className={labelClass}>Sections</p>
          {data.sections.map((section, index) => (
            <div key={index} className="space-y-2 rounded-2xl border border-[#303A4E]/70 bg-[#0B1020]/55 p-3">
              <div className="flex items-center gap-2">
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#5EDDF2]/15 text-[10px] font-black text-[#DDFBFF]">{index + 1}</span>
                <input value={section.heading || ""} onChange={(e) => setSection(index, "heading", e.target.value)}
                  placeholder="Heading" className="flex-1 rounded-lg border border-[#303A4E]/70 bg-[#0B1020]/65 px-3 py-1.5 text-sm font-bold text-[#DDFBFF] outline-none placeholder:text-[#78859A] focus:border-[#5EDDF2]/40" />
              </div>
              <textarea rows={3} value={section.body || ""} onChange={(e) => setSection(index, "body", e.target.value)} placeholder="Body" className={fieldClass} />
            </div>
          ))}
        </div>
      )}
      {data.closing !== undefined && (
        <div><label className={labelClass}>Closing</label><textarea rows={2} value={data.closing || ""} onChange={(e) => set("closing", e.target.value)} className={fieldClass} /></div>
      )}
      {data.callToAction !== undefined && (
        <div><label className={labelClass}>Call To Action</label><textarea rows={2} value={data.callToAction || ""} onChange={(e) => set("callToAction", e.target.value)} className={fieldClass} /></div>
      )}
      {data.hashtags !== undefined && (
        <div><label className={labelClass}>Hashtags</label><textarea rows={2} value={data.hashtags || ""} onChange={(e) => set("hashtags", e.target.value)} className={fieldClass} /></div>
      )}
    </div>
  );
}

// ─── Polished Generated View ───────────────────────────────────────────────
function PolishedGeneratedView({ data }: { data: ParsedGeneratedContent }) {
const hashtags =
  typeof data.hashtags === "string"
    ? data.hashtags.split(" ").map((tag) => tag.trim()).filter(Boolean)
    : Array.isArray(data.hashtags)
    ? data.hashtags
    : [];
  // Section label color cycling for highlight variety
  const sectionAccents = [
    "border-[#5EDDF2]/30 bg-[#5EDDF2]/8",
    "border-[#B6F269]/25 bg-[#B6F269]/6",
    "border-[#B9A7FF]/25 bg-[#B9A7FF]/6",
    "border-yellow-400/25 bg-yellow-400/6",
  ];
  const headingAccents = ["text-[#5EDDF2]", "text-[#B6F269]", "text-[#C4B5FD]", "text-yellow-300"];

  return (
    <div className="space-y-5">
      {data.title && (
        <div className="rounded-2xl border border-[#5EDDF2]/20 bg-gradient-to-br from-cyan-400/12 via-white/[0.03] to-lime-400/10 p-4">
          {/* Highlighted label */}
          <div className="mb-2 inline-flex items-center gap-1.5 rounded-full border border-[#5EDDF2]/25 bg-[#5EDDF2]/12 px-3 py-1">
            <span className="h-1.5 w-1.5 rounded-full bg-[#5EDDF2]" />
            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#7DEBFF]">Title</p>
          </div>
          <h2 className="bg-gradient-to-r from-cyan-200 via-lime-200 to-cyan-300 bg-clip-text text-xl font-black leading-snug text-transparent sm:text-2xl">
            {data.title}
          </h2>
        </div>
      )}

      {data.intro && (
        <div className="rounded-2xl border border-[#303A4E]/70 bg-[#1A2230]/70 p-4">
          <div className="mb-2 inline-flex items-center gap-1.5 rounded-full border border-[#9AA7BC]/20 bg-[#9AA7BC]/8 px-3 py-1">
            <span className="h-1.5 w-1.5 rounded-full bg-[#9AA7BC]" />
            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#9AA7BC]">Introduction</p>
          </div>
          <p className="text-[13px] leading-6 text-[#E5EAF2]">{data.intro}</p>
        </div>
      )}
       {(data as any).body && ( <div className="rounded-2xl border border-[#303A4E]/70 bg-[#111A2B]/70 p-4"> <div className="mb-2 inline-flex items-center gap-1.5 rounded-full border border-[#5EDDF2]/25 bg-[#5EDDF2]/10 px-3 py-1"> <span className="h-1.5 w-1.5 rounded-full bg-[#5EDDF2]" /> <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#7DEBFF]"> Main Content </p> </div> <div className="whitespace-pre-wrap text-[13px] leading-7 text-[#E5EAF2]"> {(data as any).body} </div> </div> )}
      {data.sections && data.sections.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[#5EDDF2]/20 to-transparent" />
            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#9AA7BC]">Content Sections</p>
            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[#5EDDF2]/20 to-transparent" />
          </div>

          {data.sections.map((section, index) => (
            <motion.div
              key={`${section.heading}-${index}`}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.04 }}
              className={`rounded-2xl border p-4 hover:shadow-[0_0_20px_rgba(94,221,242,0.08)] transition-shadow ${sectionAccents[index % sectionAccents.length]}`}
            >
              <div className="mb-2 flex items-start gap-3">
                <div className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#0B1020]/60 text-[10px] font-black ${headingAccents[index % headingAccents.length]}`}>
                  {index + 1}
                </div>
                <div className="min-w-0">
                  {section.heading && (
                    <h3 className={`text-sm font-black ${headingAccents[index % headingAccents.length]}`}>
                      {section.heading}
                    </h3>
                  )}
                  {section.body && (
                    <p className="mt-2 text-[13px] leading-6 text-[#C4CEDD]">{section.body}</p>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {data.closing && (
        <div className="rounded-2xl border border-[#B6F269]/20 bg-[#B6F269]/6 p-4">
          <div className="mb-2 inline-flex items-center gap-1.5 rounded-full border border-[#B6F269]/25 bg-[#B6F269]/12 px-3 py-1">
            <span className="h-1.5 w-1.5 rounded-full bg-[#B6F269]" />
           <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#B6F269]"> Conclusion</p>

          </div>
          <p className="text-[13px] leading-6 text-[#E5EAF2]">{data.closing}</p>
        </div>
      )}

      {data.callToAction && (
        <div className="rounded-2xl border border-[#5EDDF2]/20 bg-gradient-to-r from-cyan-400/10 to-lime-400/10 p-4">
          <div className="mb-2 flex items-center gap-2">
            <div className="inline-flex items-center gap-1.5 rounded-full border border-[#5EDDF2]/30 bg-[#5EDDF2]/15 px-3 py-1">
              <Megaphone size={11} className="text-[#5EDDF2]" />
              <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#DDFBFF]">Call To Action</p>
            </div>
          </div>
          <p className="text-[13px] font-semibold leading-6 text-cyan-50">{data.callToAction}</p>
        </div>
      )}

      {hashtags.length > 0 && (
        <div>
          <div className="mb-2 flex items-center gap-2">
            <Hash size={14} className="text-[#9AA7BC]" />
            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#9AA7BC]">Hashtags</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {hashtags.map((tag, index) => (
              <span key={`${tag}-${index}`} className="rounded-full border border-[#5EDDF2]/20 bg-[#5EDDF2]/10 px-3 py-1.5 text-[11px] font-bold text-[#DDFBFF]">
                {tag}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Blog Workflow ─────────────────────────────────────────────────────────
function ContentBlogWorkflow({
  showBlogFlow,
  addedToClone,
  blogPreview,
  blogFormatting,
  publishedBlog,
  imageLoading,
  formatLoading,
  publishLoading,
  canPublishBlog,
  hasFormattedBlogPreview,
isBlogModalOpen,
  onShowBlogFlow,
  onCreateImage,
  onFormatBlog,
  onPublishBlog,
  onChangePreview,
  onSetBlogModalOpen,
  onReset,
}: {
  showBlogFlow: boolean;
addedToClone: boolean;
blogPreview: BlogPayload | null;
blogFormatting: boolean;
publishedBlog: BlogPayload | null;
imageLoading: boolean;
formatLoading: boolean;
publishLoading: boolean;
canPublishBlog: boolean;
hasFormattedBlogPreview: boolean;
isBlogModalOpen: boolean;
onSetBlogModalOpen: (v: boolean) => void;
  onShowBlogFlow: () => void; onCreateImage: () => void; onFormatBlog: () => void; onPublishBlog: () => void;
  onChangePreview: (data: BlogPayload) => void; onReset: () => void;
}) {
  useEffect(() => {
  if (blogPreview && hasFormattedBlogPreview && !publishedBlog) onSetBlogModalOpen(true);
}, [blogPreview, hasFormattedBlogPreview, publishedBlog]);



  if (publishedBlog) {
    return (
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
        className="rounded-[24px] border border-lime-300/20 bg-lime-300/8 p-4">
        <div className="mb-3 flex items-center gap-2 text-lime-100">
          <CheckCircle size={18} />
          <h3 className="text-sm font-black">Blog Published Successfully</h3>
        </div>
        <p className="text-xs leading-5 text-lime-50/80">
          Blog Post ID: {publishedBlog.blogPostId || "POSTED SUCCESSFULLY"}
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          <button type="button" onClick={onReset}
            className="inline-flex h-10 items-center gap-2 rounded-xl bg-gradient-to-br from-[#B6F269] via-[#75E6C9] to-[#5EDDF2] px-4 text-xs font-black text-black">
            <RotateCcw size={14} />
            New Content
          </button>
        </div>
      </motion.div>
    );
  }

if (!showBlogFlow) {
  return (
    <>
      <ActionQuestion
        text={addedToClone ? "Content is added to clone. Do you want to create a blog now?" : "Do you want to create a blog for this generated content?"}
        button="Yes, Open Blog Workflow"
        loading={false}
        onClick={onShowBlogFlow}
      />
      <BlogPreviewModal
      open={isBlogModalOpen || blogFormatting}

        blogFormatting={blogFormatting}
        blogPreview={blogPreview}
        canPublishBlog={canPublishBlog}
        publishLoading={publishLoading}
        imageLoading={imageLoading}
        onCreateImage={onCreateImage}
       onClose={() => onSetBlogModalOpen(false)}

        onPublish={onPublishBlog}
        onChangePreview={onChangePreview}
      />
    </>
  );
}

return (
  <>
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      className="rounded-[24px] border border-[#3A465B]/70 bg-gradient-to-br from-[#0B1020]/85 via-[#111A2B]/80 to-[#0B1020]/85 p-3 shadow-[0_18px_50px_rgba(0,0,0,0.28)] sm:p-4"
    >
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="flex items-center gap-2 text-sm font-black text-[#DDFBFF]">
            <Newspaper size={16} />
            Blog Posting Workflow
          </p>
          <p className="mt-1 text-xs text-[#9AA7BC]">Format preview, optionally add image, then publish.</p>
        </div>
        <span className={`w-fit rounded-full border px-3 py-1.5 text-[11px] font-black ${
          addedToClone ? "border-lime-300/25 bg-lime-300/10 text-lime-100" : "border-yellow-300/25 bg-yellow-300/10 text-yellow-100"
        }`}>
          {addedToClone ? "Clone: Added" : "Clone: Not Added"}
        </span>
      </div>
      <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
        <WorkflowButton label={hasFormattedBlogPreview ? "Refresh Blog Preview" : "Format Blog Preview"} icon={<FileText size={15} />} loading={formatLoading} disabled={false} onClick={onFormatBlog} />
<WorkflowButton label="Open Preview Modal" icon={<Eye size={15} />} loading={false} disabled={!blogPreview} onClick={() => onSetBlogModalOpen(true)} />
        <WorkflowButton label="Publish Blog" icon={<Send size={15} />} loading={publishLoading} disabled={!canPublishBlog} onClick={onPublishBlog} />
      </div>
      {!blogPreview ? (
        <div className="mt-4 rounded-2xl border border-dashed border-[#3A465B]/70 bg-[#07111f]/70 p-4 text-center text-xs text-[#9AA7BC]">
          Click Format Blog Preview to prepare the blog.
        </div>
      ) : (
        <div className="mt-4 rounded-2xl border border-[#5EDDF2]/20 bg-[#07111f]/70 p-3 sm:p-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <p className="truncate text-sm font-black text-[#DDFBFF]">
                {blogPreview.title || "Blog preview is ready"}
              </p>
              <p className="mt-1 text-xs text-[#9AA7BC]">
                Review/edit the blog inside modal, then publish.
              </p>
            </div>
         <button type="button" onClick={() => onSetBlogModalOpen(true)}
  className="inline-flex h-10 items-center justify-center gap-2 rounded-2xl bg-gradient-to-br from-[#B6F269] via-[#75E6C9] to-[#5EDDF2] px-4 text-xs font-black text-black">
  <Eye size={15} />
  View Blog Preview
</button>
          </div>
        </div>
      )}
    </motion.div>

   <BlogPreviewModal
  open={isBlogModalOpen || blogFormatting}
  blogFormatting={blogFormatting}
  blogPreview={blogPreview}
  canPublishBlog={canPublishBlog}
  publishLoading={publishLoading}
  imageLoading={imageLoading}
  onCreateImage={onCreateImage}
  onClose={() => onSetBlogModalOpen(false)}
  onPublish={onPublishBlog}
  onChangePreview={onChangePreview}
/>
  </>
);
}

// ─── Blog Preview Modal (reduced ~10%, with inline edit) ───────────────────
function BlogPreviewModal({
  open,
  blogFormatting,
  blogPreview,
  canPublishBlog,
  publishLoading,
  imageLoading,
  onCreateImage,
  onClose,
  onPublish,
  onChangePreview,
}: {
  open: boolean;
blogFormatting: boolean;
blogPreview: BlogPayload | null;
  canPublishBlog: boolean;
  publishLoading: boolean;
  imageLoading: boolean;
  onCreateImage: () => void;
  onClose: () => void;
  onPublish: () => void;
  onChangePreview: (data: BlogPayload) => void;
}) {

  const [isEditingBlog, setIsEditingBlog] = useState(false);

const [tempPreview, setTempPreview] =
  useState<BlogPayload>({} as BlogPayload);

useEffect(() => {
  if (blogPreview) {
    setTempPreview(blogPreview);
  }
}, [blogPreview]);

if (!open && !blogFormatting) return null;
const preview = blogPreview ?? ({} as BlogPayload);
if (blogFormatting) {
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 px-2 py-3 backdrop-blur-md"
      >
        <motion.div
          initial={{ opacity: 0, y: 28, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 18, scale: 0.96 }}
          transition={{ duration: 0.22 }}
          className="flex max-h-[88vh] w-full max-w-[980px] flex-col overflow-hidden rounded-[24px] border border-[#3A465B]/80 bg-[#0B1020] shadow-[0_30px_120px_rgba(0,0,0,0.65)]"
        >
          {/* Modal Header - same as normal preview */}
          <div className="sticky top-0 z-10 border-b border-[#303A4E]/80 bg-[#111A2B]/95 p-2.5 backdrop-blur-xl sm:p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="flex items-center gap-2 text-[13px] font-black text-[#DDFBFF] sm:text-sm">
                  <Newspaper size={15} />
                  Blog Preview
                </p>
                <p className="mt-0.5 text-[11px] text-[#9AA7BC]">AI is formatting your blog content...</p>
              </div>
              <button type="button" onClick={onClose}
                className="inline-flex h-9 items-center justify-center gap-1.5 rounded-xl border border-[#3A465B]/80 bg-[#1A2230] px-3 text-[11px] font-black text-white">
                <X size={13} />
                Close
              </button>
            </div>
          </div>

          {/* AI Reasoning Loader fills the modal body */}
          <div className="flex-1 p-4">
            <AIReasoningLoader />
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 px-2 py-3 backdrop-blur-md"
      >
        <motion.div
          initial={{ opacity: 0, y: 28, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 18, scale: 0.96 }}
          transition={{ duration: 0.22 }}
          // ~10% smaller: max-w reduced from 1120px, text sizes reduced
          className="flex max-h-[88vh] w-full max-w-[980px] flex-col overflow-hidden rounded-[24px] border border-[#3A465B]/80 bg-[#0B1020] shadow-[0_30px_120px_rgba(0,0,0,0.65)]"
        >
          {/* Modal Header */}
          <div className="sticky top-0 z-10 border-b border-[#303A4E]/80 bg-[#111A2B]/95 p-2.5 backdrop-blur-xl sm:p-3">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <p className="flex items-center gap-2 text-[13px] font-black text-[#DDFBFF] sm:text-sm">
                  <Newspaper size={15} />
                  Blog Preview
                </p>
                <p className="mt-0.5 text-[11px] text-[#9AA7BC]">Review and edit all fields, then publish.</p>
              </div>
              <div className="flex flex-col gap-1.5 sm:flex-row sm:items-center"> {!isEditingBlog ? ( <button type="button" onClick={() => setIsEditingBlog(true)} className="inline-flex h-9 items-center justify-center gap-1.5 rounded-xl border border-[#5EDDF2]/30 bg-[#5EDDF2]/10 px-3 text-[11px] font-black text-[#DDFBFF]" > <Edit3 size={13} /> Edit </button> ) : ( 
                <> 
                <button type="button" onClick={() => {
  if (blogPreview) {
    setTempPreview(blogPreview);
  }
  setIsEditingBlog(false);
}}
              className="inline-flex h-9 items-center justify-center gap-1.5 rounded-xl bg-gradient-to-br from-[#B6F269] via-[#75E6C9] to-[#5EDDF2] px-3 text-[11px] font-black text-black" > <Save size={13} /> Save </button> <button type="button" onClick={() => { setTempPreview(blogPreview ?? {} as BlogPayload); setIsEditingBlog(false); }}
               className="inline-flex h-9 items-center justify-center gap-1.5 rounded-xl border border-[#3A465B]/80 bg-[#1A2230] px-3 text-[11px] font-black text-white" > <X size={13} /> Cancel </button> </> )}
                <button type="button" onClick={onPublish} disabled={!canPublishBlog || publishLoading}
                  className="inline-flex h-9 items-center justify-center gap-1.5 rounded-xl bg-gradient-to-br from-[#B6F269] via-[#75E6C9] to-[#5EDDF2] px-3 text-[11px] font-black text-black disabled:cursor-not-allowed disabled:opacity-45">
                  {publishLoading ? <Loader2 className="animate-spin" size={13} /> : <Send size={13} />}
                  {publishLoading ? "Publishing..." : "Publish Blog"}
                </button>
                <button type="button" onClick={onClose}
                  className="inline-flex h-9 items-center justify-center gap-1.5 rounded-xl border border-[#3A465B]/80 bg-[#1A2230] px-3 text-[11px] font-black text-white">
                  <X size={13} />
                  Close
                </button>
              </div>
            </div>
          </div>

          {/* Modal Body */}
          <div className="overflow-y-auto p-2.5 sm:p-3">
            <div className="grid gap-3 lg:grid-cols-[0.85fr_1.15fr]">
              {/* Left: image + status */}
              <div className="space-y-2.5">
               {imageLoading ? (

  <div className="flex min-h-[260px] flex-col items-center justify-center rounded-2xl border border-[#5EDDF2]/20 bg-[#07111f]/70">

    <div className="relative mb-4 flex h-20 w-20 items-center justify-center">
      {[0,1,2].map((i) => (
        <span
          key={i}
          className="absolute inset-0 rounded-full border border-cyan-400/30"
          style={{
            animation: `ping 2s ${i * 0.4}s infinite`,
          }}
        />
      ))}

      <Image
        size={28}
        className="animate-pulse text-cyan-300"
      />
    </div>

    <p className="text-sm font-bold text-cyan-300">
      AI Generating Image...
    </p>

    <p className="mt-1 text-xs text-slate-400">
      Creating blog artwork
    </p>

    <div className="mt-4 flex gap-1">
      {[0,1,2].map((i) => (
        <span
          key={i}
          className="h-2 w-2 rounded-full bg-cyan-400 animate-bounce"
          style={{
            animationDelay: `${i * 0.2}s`,
          }}
        />
      ))}
    </div>

  </div>

) : blogPreview?.imageUrl ? (
  <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-[#07111f]">

    <img
      src={getImageUrl(blogPreview?.imageUrl || "")}
      alt="Generated blog"
      className="max-h-[330px] w-full object-cover"
    />

    <button
      type="button"
      onClick={onCreateImage}
      className="absolute bottom-3 right-3 flex items-center gap-2 rounded-xl bg-gradient-to-r from-[#5EDDF2] to-[#B6F269] px-3 py-2 text-[11px] font-black text-black"
    >
      <Sparkles size={14} />
      Regenerate
    </button>

  </div>

) : (

  <div className="flex min-h-[260px] flex-col items-center justify-center gap-4 rounded-2xl border border-dashed border-[#3A465B]/70 bg-[#07111f]/70">

    <Image
      size={40}
      className="text-[#5EDDF2]"
    />

    <p className="text-[12px] text-[#9AA7BC]">
      No image generated yet
    </p>

    <button
      type="button"
      onClick={onCreateImage}
      disabled={imageLoading}
      className="flex items-center gap-2 rounded-xl bg-gradient-to-r from-[#5EDDF2] to-[#B6F269] px-4 py-2 text-[11px] font-black text-black"
    >
      <Sparkles size={14} />
      Generate Image
    </button>

  </div>

)}
                <div className="rounded-2xl border border-[#303A4E]/70 bg-[#111A2B]/80 p-3">
                  <p className="mb-2 text-[10px] font-bold uppercase tracking-[0.16em] text-[#9AA7BC]">Preview Status</p>
                  <div className="grid gap-1.5 text-[11px] text-[#C4CEDD]">
                    <p>Title: <span className="font-bold text-white">{blogPreview?.title ? "✓ Ready" : "✗ Missing"}</span></p>
                    <p>Description: <span className="font-bold text-white">{blogPreview?.description ? "✓ Ready" : "✗ Missing"}</span></p>
                    <p>Captions: <span className="font-bold text-white">{blogPreview?.socialMediaCaptions ? "✓ Ready" : "✗ Missing"}</span></p>
                    <p>Image: <span className="font-bold text-white">{blogPreview?.imageUrl ? "✓ Ready" : "⚠ Optional"}</span></p>
                  </div>
                </div>
              </div>
              {/* Right: editable fields */}
              <EditableBlogPreview data={tempPreview} onChange={setTempPreview} isEditing={isEditingBlog} />
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

// ─── Workflow Button ───────────────────────────────────────────────────────
function WorkflowButton({ label, icon, loading, disabled, onClick }: { label: string; icon: React.ReactNode; loading?: boolean; disabled?: boolean; onClick: () => void }) {
  return (
    <motion.button
      whileHover={!disabled ? { y: -2, scale: 1.01 } : undefined}
      whileTap={!disabled ? { scale: 0.97 } : undefined}
      type="button" onClick={onClick} disabled={disabled || loading}
      className="flex min-h-[54px] items-center justify-center gap-2 rounded-2xl border border-[#5EDDF2]/20 bg-[#5EDDF2]/10 px-3 text-xs font-black text-[#DDFBFF] disabled:cursor-not-allowed disabled:opacity-45"
    >
      {loading ? <Loader2 className="animate-spin" size={15} /> : icon}
      {loading ? "Processing..." : label}
    </motion.button>
  );
}

// ─── Action Question ───────────────────────────────────────────────────────
function ActionQuestion({ text, button, loading, onClick }: { text: string; button: string; loading?: boolean; onClick: () => void }) {
  return (
    <div className="rounded-[24px] border border-[#5EDDF2]/25 bg-gradient-to-br from-[#5EDDF2]/12 via-[#101827]/80 to-[#B6F269]/10 p-4 shadow-[0_16px_40px_rgba(94,221,242,0.10)]">
      <p className="mb-3 text-sm font-bold text-cyan-50">{text}</p>
      <button type="button" onClick={onClick} disabled={loading}
        className="inline-flex h-10 items-center gap-2 rounded-2xl bg-gradient-to-br from-[#B6F269] via-[#75E6C9] to-[#5EDDF2] px-4 text-xs font-black text-black disabled:opacity-50">
        {loading ? <Loader2 className="animate-spin" size={15} /> : <Newspaper size={15} />}
        {button}
      </button>
    </div>
  );
}

// ─── Editable Blog Preview ─────────────────────────────────────────────────
function EditableBlogPreview({
  data,
  onChange,
  isEditing,
}: {
  data: BlogPayload;
  onChange: (data: BlogPayload) => void;
  isEditing: boolean;
}) {

  const update = (key: keyof BlogPayload, value: string) =>
    onChange({
      ...data,
      [key]: value,
    });

  const titleValue =
    data.title?.trim() || "radhAI Generated Blog";

  const descriptionValue =
    data.description?.trim() ||
    "Blog content generated from radhAI admin input.";

 const extractedTags =
  data.description?.match(/#[a-zA-Z0-9_]+/g)?.join(" ") || "";

const captionsValue = `
${data.socialMediaCaptions?.trim() || ""}
${extractedTags}
`.trim();

  const hashtags =
    captionsValue.match(/#[a-zA-Z0-9_]+/g) || [];

  return (
    <div className="space-y-5">

      {/* BLOG TITLE */}
      <div>
        <label className="mb-2 block text-[12px] font-black uppercase tracking-[0.18em] text-[#9AA7BC]">
          BLOG TITLE
        </label>

        {isEditing ? (
          <input
            value={titleValue}
            onChange={(e) =>
              update("title", e.target.value)
            }
            className="h-14 w-full rounded-2xl border border-[#303A4E]/70 bg-[#07111f] px-5 text-sm text-white outline-none focus:border-[#5EDDF2]/50"
          />
        ) : (
          <div className="flex min-h-[56px] items-center rounded-2xl border border-[#303A4E]/70 bg-[#07111f] px-5 text-sm text-white">
            {titleValue}
          </div>
        )}
      </div>

      {/* DESCRIPTION */}
      <div>
        <label className="mb-2 block text-[12px] font-black uppercase tracking-[0.18em] text-[#9AA7BC]">
          DESCRIPTION
        </label>

        {isEditing ? (
          <textarea
            value={descriptionValue}
            onChange={(e) =>
              update("description", e.target.value)
            }
            className="h-[240px] w-full resize-none overflow-y-auto rounded-2xl border border-[#303A4E]/70 bg-[#07111f] p-5 text-sm leading-7 text-white outline-none focus:border-[#5EDDF2]/50"
          />
        ) : (
          <div className="h-[240px] overflow-y-auto whitespace-pre-wrap rounded-2xl border border-[#303A4E]/70 bg-[#07111f] p-5 text-sm leading-7 text-white">
            {descriptionValue}
          </div>
        )}
      </div>

      {/* SOCIAL MEDIA */}
      <div>
        <label className="mb-2 block text-[12px] font-black uppercase tracking-[0.18em] text-[#9AA7BC]">
          SOCIAL MEDIA CAPTIONS
        </label>

        {isEditing ? (
          <textarea
            value={captionsValue}
            onChange={(e) =>
              update(
                "socialMediaCaptions",
                e.target.value
              )
            }
            className="h-[150px] w-full resize-none overflow-y-auto rounded-2xl border border-[#303A4E]/70 bg-[#07111f] p-5 text-sm leading-7 text-white outline-none focus:border-[#5EDDF2]/50"
          />
        ) : (
          <div className="h-[150px] overflow-y-auto whitespace-pre-wrap rounded-2xl border border-[#303A4E]/70 bg-[#07111f] p-5 text-sm leading-7 text-white">
            {captionsValue}
          </div>
        )}

        {/* HASHTAGS */}
       
      </div>

      {/* ADDED BY */}
      <div>
        <label className="mb-2 block text-[12px] font-black uppercase tracking-[0.18em] text-[#9AA7BC]">
          ADDED BY
        </label>

        {isEditing ? (
          <input
            value={data.addedBy || "Radha"}
            onChange={(e) =>
              update("addedBy", e.target.value)
            }
            className="h-14 w-full rounded-2xl border border-[#303A4E]/70 bg-[#07111f] px-5 text-sm text-white outline-none focus:border-[#5EDDF2]/50"
          />
        ) : (
          <div className="flex min-h-[56px] items-center rounded-2xl border border-[#303A4E]/70 bg-[#07111f] px-5 text-sm text-white">
            {data.addedBy || "Radha"}
          </div>
        )}
      </div>

      {/* IMAGE URL */}
      <div>
        <label className="mb-2 block text-[12px] font-black uppercase tracking-[0.18em] text-[#9AA7BC]">
          IMAGE URL
        </label>

        {isEditing ? (
          <input
            value={getImageUrl(data.imageUrl)}
            onChange={(e) =>
              update("imageUrl", e.target.value)
            }
            className="h-14 w-full rounded-2xl border border-[#303A4E]/70 bg-[#07111f] px-5 text-sm text-white outline-none focus:border-[#5EDDF2]/50"
          />
        ) : (
          <div className="overflow-x-auto rounded-2xl border border-[#303A4E]/70 bg-[#07111f] p-5 text-sm text-white">
            {getImageUrl(data.imageUrl)}
          </div>
        )}
      </div>
    </div>
  );
}

function BlogTextarea({ label, value, onChange, rows = 4 }: { label: string; value: string; onChange: (value: string) => void; rows?: number }) {
  return (
    <div>
      <label className="mb-1 block text-[10px] font-bold uppercase tracking-[0.16em] text-[#9AA7BC]">{label}</label>
      <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={rows}
        className="w-full resize-y rounded-xl border border-[#303A4E]/70 bg-[#07111f] p-2.5 text-[11px] leading-5 text-white outline-none focus:border-[#5EDDF2]/50" />
    </div>
  );
}


function PreviewField({
  label,
  value,
  multiline = false,
}: {
  label: string;
  value: string;
  multiline?: boolean;
}) {
  return (
    <div>
      <label className="mb-1 block text-[10px] font-bold uppercase tracking-[0.16em] text-[#9AA7BC]">
        {label}
      </label>

      <div
        className={`rounded-xl border border-[#303A4E]/70 bg-[#07111f] p-3 text-[11px] text-white ${
          multiline
            ? "max-h-[220px] overflow-y-auto whitespace-pre-wrap leading-6"
            : "truncate"
        }`}
      >
        {value || "-"}
      </div>
    </div>
  );
}


// ─── Glass Card ───────────────────────────────────────────────────────────
function GlassCard({ children }: { children: React.ReactNode }) {
  return (
    <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl border border-[#3A465B]/70 bg-[#1C2433]/82 p-3 shadow-[0_18px_60px_rgba(0,0,0,.32)] backdrop-blur-2xl sm:p-4">
      {children}
    </motion.div>
  );
}
function ActionButton({
  label,
  onClick,
  disabled,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  const isLoading = label === "Uploading";

  return (
    <motion.button
      whileHover={!disabled ? { y: -2, scale: 1.015 } : undefined}
      whileTap={!disabled ? { scale: 0.97 } : undefined}
      onClick={onClick}
      disabled={disabled}
className={`flex h-11 items-center justify-center rounded-xl bg-gradient-to-br from-[#B6F269] via-[#75E6C9] to-[#5EDDF2] px-4 text-xs font-black text-black transition-all duration-300 disabled:cursor-not-allowed ${
  isLoading
    ? "shadow-[0_0_40px_rgba(182,242,105,1),0_0_80px_rgba(94,221,242,0.9)]"
    : "shadow-[0_14px_32px_rgba(94,221,242,0.20)]"
}`}  >
      {isLoading && (
        <Loader2
          size={16}
          className="mr-2 animate-spin"
        />
      )}

{isLoading ? "✨ Uploading..." : label}
    </motion.button>
  );
}

// ─── File Chip ─────────────────────────────────────────────────────────────
function FileChip({ icon, name, onRemove }: { icon: React.ReactNode; name: string; onRemove: () => void }) {
  return (
    <div className="flex max-w-full items-center gap-2 rounded-full border border-[#3A465B]/70 bg-[#0B1020]/65 px-3 py-2 text-[11px] text-[#E5EAF2]">
      <span className="shrink-0 text-[#7DEBFF]">{icon}</span>
      <span className="max-w-[180px] truncate sm:max-w-[260px]">{name}</span>
      <button type="button" onClick={onRemove} className="rounded-full bg-red-500/15 p-1 text-red-200 hover:bg-red-500/25">
        <X size={12} />
      </button>
    </div>
  );
}